import { Component, SimpleChanges, Input, Output, EventEmitter, OnChanges, ElementRef, ChangeDetectionStrategy, ViewEncapsulation, Renderer2, SecurityContext, AfterViewInit, OnDestroy } from '@angular/core';
import { LsLabel, wrap } from './ls-label.class';
import { ScaleLinear, scaleLinear, ScaleBand, Axis, Selection, scaleBand, axisTop, pointer, select } from "d3";
import { LsWidgetRender, W_LinescaleRender, LsItem } from "core-lib";
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgIf } from '@angular/common';

// declare var gsap: any;

export interface LinescaleInput {
    scale_text: string[];
    items: { label: string, value: number };
    value?: number
}

@Component({
    selector: 'linescale',
    // changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div *ngIf="prelim" [innerHTML]="prelim"></div>
        <div *ngIf="text" [innerHTML]="text"></div>
    `,
    styles: [`

        linescale {
            display: block;
            width: 700px;
        }

        h3 {
            font-family: Roboto, "Helvetica Neue", sans-serif;
            font-weight: 300;
        }

        .text-content {
            padding: 8px;
        }

        .chart-controls button {
            margin-right: 8px;
        }

        .chart-box {
            display: block;
            width: 100%;
            position: relative;
        }

        .chart-box p {
            margin: 0;
        }


        .lift-ticks {
            transform: translateY(50);
        }

        .axis-label {
            font-size: 16px;
            transform: translate(0, 20);
            font-family: Roboto;
        }

        .tick-mark {
            display: none;
        }


        .tail {
            fill: white;
        }

        .text-box {
            box-sizing: border-box;
            position: absolute;
            display: inline-block;
        }

        .text-box.right {
            text-align: end;
        }

        .item-line {
            stroke: rgba(0, 0, 0, 0.7);
        }

        .ls-line:hover {
            cursor: pointer;
        }

        text.anchor {
            fill: #3f51b5;
            font-weight: 600;
        }

        .item-line.anchor {
            stroke: #3f51b5;
        }
    `],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [NgIf]
})
export class LinescaleComponent implements OnChanges, AfterViewInit, OnDestroy {

    @Input() lsData: W_LinescaleRender;
    @Input() isQuestion?: boolean;
    @Input() skipUrl: string;
    @Input() limit: number;
    @Input() vMargin: number = 0;

    @Output() scored = new EventEmitter<{ [id: string]: number }>();
    @Output() skip = new EventEmitter<void>();

    // TODO: Temporary until data returns rated items.
    private _isQuestion: boolean = false;
    readonly ratedItems: { [id: string]: number } = {};
    readonly results: any[];
    text: string | SafeHtml;
    prelim: string | SafeHtml;

    readonly margin = {
        top: 75,
        right: 25,
        // right: 0,
        bottom: 25,
        // left: 0
        left: 25
    };

    readonly fullWidth: number = 700;
    readonly fullHeight: number = 650;
    readonly height: number = this.fullHeight - this.margin.bottom - this.margin.top;
    readonly axisYOffset: number = 100;
    readonly axisXOffset: number = 19;
    readonly axisHeight: number = 8;
    readonly strokeWidth: number = 4;
    readonly trianglePoints = {
        pointA: [this.strokeWidth, this.axisYOffset + this.axisHeight / 2],
        pointB: [this.axisXOffset - this.strokeWidth / 2, this.axisYOffset + this.axisHeight + 2],
        pointC: [this.axisXOffset - this.strokeWidth / 2, this.axisYOffset - 2],
    };

    private readonly items: LsLabel[] = [];
    svg;
    x: ScaleLinear<number, number> = scaleLinear()
        // .domain([724, 0])
        .domain([19, 743])
        .range([0, this.linescaleWidth - this.axisXOffset])
        .clamp(true);

    xLabel: ScaleBand<any>;
    // .range(['gran', 'red', 'blue']);

    xAxisCall: Axis<string>;
    chartBox: Selection<HTMLBaseElement, any, any, any>;
    fillColor: string = '#3f51b5';

    private line: Selection<SVGRectElement, string, any, any>;
    /**
     * Score value (currently, may change to coordinate value)
     * UPDATE #1: MUST BE coordinate value due to reversed domain
     */
    private threshhold: number = 480;
    private lowestRating: LsLabel;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private sanitizer: DomSanitizer
    ) { }

    get arrowWidth(): number {
        return this.axisXOffset;
    }

    get linescaleWidth(): number {
        return this.fullWidth - this.margin.left - this.margin.right;
    }

    /**
     * Returns labels in rendering order
     * @param labels List of labels
     */
    private sortLabels(labels: LsLabel[]): LsLabel[] {
        return this.items.sort((a, b) => a.score - b.score);
        // List of labels to be right aligned
        const aboveThresh: LsLabel[] = this.items
            .filter(item => this.x(item.score) > this.threshhold)
            .sort((a, b) => this.x(a.score) - this.x(b.score));
        // List of labels to be left aligned
        const belowThresh: LsLabel[] = this.items
            .filter(item => this.x(item.score) <= this.threshhold)
            .sort((a, b) => this.x(a.score) - this.x(b.score));

        try {
            const dir = labels[0].determineRenderDir();
            // Render low ratings to the left
            console.log('Reversing thresh items: ', dir);

            // if (dir === 'right')
            //     belowThresh.reverse();
        } catch (error) {
            // Render low ratings to the left
            // withinThresh.reverse();
            console.log('Error on linescale sort');

        }

        // Sorts the items into descending score order and replaces the items in the SAME array object with new sorted array items
        return aboveThresh.concat(belowThresh);
    }

    private addRating(text: string, value: number, threshhold: number, isAnchor: boolean = false): void {
        const label = new LsLabel(value, text, {
            axis: {
                yPos: this.axisYOffset + this.axisHeight,
                xPos: this.axisXOffset
            },
            linescaleWidth: this.linescaleWidth,
            height: this.height,
            xScale: this.x,
            svg: this.svg,
            marginX: this.margin.left,
            allLabels: this.items,
            threshhold,
            vBoxWidth: this.fullWidth,
            marginY: this.vMargin
        }, isAnchor);
        this.items.push(label);

        // Keep track of lowest rating
        if (this.lowestRating) {
            if (value < this.lowestRating.score)
                this.lowestRating = label;
        } else
            this.lowestRating = label;
    }

    /**
     * Called every time ratings need to be rendered
     */
    private renderLabels(): void {
        if (!this.items.length)
            return;

        // Sorts the items into descending score order and replaces the items in the SAME array object with new sorted array items
        // TODO: Possibly move this out to the initializer 'onChanges' routine
        this.items.splice(0, this.items.length, ...this.sortLabels(this.items));
        // console.log(this.items);


        // If more than one rating loop through and render from lowest score to highest to allow propper alignment of text and resizing of svg element
        if (this.items.length > 1) {
            let itemA: LsLabel, itemB: LsLabel;

            for (let x = this.items.length - 1; x > 0; x--) {
                itemB = this.items[x];
                itemA = this.items[x - 1];
                // console.log(itemB.text);

                itemB.render();
                itemA.boxY = itemB.boxY + itemB.boxHeight + 8;
            }
        }
        // Render the highest rated item which is not included the loop. Will also render in the case of only a single rating in list
        const highestRated: LsLabel = this.items[0];
        highestRated.render();
        this.resizeSVG();

    }

    /**
     * Resizes the main SVG container to fit the items drawn
     */
    private resizeSVG(): void {

        const first: LsLabel = this.items[0];
        const nextHeight = first.boxY + first.boxHeight + 16;

        this.svg.attr('height', nextHeight)
            .attr('viewBox', `0 0 700 ${nextHeight}`);

    }

    private xPos(value: number): number {
        return this.x(value) + this.axisXOffset;
    }

    private drawScore(value: number, desc: number): void {
        // console.log('xPos: ', this.xPos(value));
        // console.log('Desc: ', desc);


        this.svg.append('rect')
            .attr('x', this.xPos(value))
            .attr('y', this.axisYOffset - 5)
            .attr('height', 25)
            .attr('width', 3)
            .style('fill: blue;')
        this.svg.append('text')
            .attr('x', this.xPos(value))
            .attr('y', this.axisYOffset + this.axisHeight + 25)
            .text(desc);
    }

    private drawLinescale(scaleText: string[], height: number): void {
        const ctrlThis = this;

        this.xLabel = scaleBand()
            .domain(scaleText)
            .range([0, this.linescaleWidth - this.axisXOffset]);
        // .paddingInner(0.5);
        // .paddingOuter(0);
        this.xAxisCall = axisTop(this.xLabel)
            .ticks(scaleText.length)
            .tickSize(8)
            .tickSizeOuter(0);

        this.svg = this.chartBox
            .append("svg")
            .attr("width", '100%')
            .attr("height", height)
            // Always hardcoded for vector coordinates
            .attr("viewBox", `0 0 ${this.fullWidth} 158`);

        const axis: Selection<SVGGElement, string, any, any> = this.svg.append('g')
            .attr('class', 'x axis')
            .call(this.xAxisCall)
            // .attr('height', 100)
            // .attr('width', this.linescaleWidth - this.arrowWidth)
            .attr('transform', `translate(${this.axisXOffset}, ${this.axisYOffset})`);

        this.line = this.svg.append('rect')
            .attr('width', this.linescaleWidth - this.axisXOffset)
            .attr('height', this.axisHeight)
            .attr('fill', this.fillColor)
            // .attr('transform', `translate(${this.axisXOffset}, ${this.axisYOffset})`);
            .attr('transform', `translate(${this.axisXOffset}, ${this.axisYOffset})`);

        const clickLine: Selection<SVGRectElement, string, any, any> = this.svg.append('rect')
            // .attr('width', this.linescaleWidth - this.axisXOffset)
            .attr('width', this.linescaleWidth - this.arrowWidth)
            .attr('height', this.axisHeight + 15)
            .attr('fill', 'transparent')
            .attr('class', 'ls-line')
            .attr('transform', `translate(${this.arrowWidth}, ${this.axisYOffset - 5})`);


        const { pointA, pointB, pointC } = this.trianglePoints;
        this.svg.append('polygon')
            .attr('points', `${pointA[0]},${pointA[1]} ${pointB[0]},${pointB[1]} ${pointC[0]},${pointC[1]}`)
            .attr('style', `fill: ${this.fillColor}; stroke: ${this.fillColor}; stroke-linejoin:round; stroke-width: ${this.strokeWidth}`);

        clickLine.on('click', function clickStatus() {
            if (!ctrlThis._isQuestion)
                return;
            else if (ctrlThis.ratedItems[ctrlThis.lsData.id])
                return;
            const myMouse: number[] = pointer(this);
            const value: number = Math.floor(ctrlThis.x.invert(myMouse[0]));

            // TODO: This should be called for standalone linescales. However NgSwitch directive in LsScreen component seems to rebuild component on expression change even for same value. Investigate
            if (ctrlThis.lsData.rs_id) {
                // ctrlThis.addLabel(ctrlThis.lsData.label, value, ctrlThis.threshhold);
            }
            ctrlThis.renderLabels();
            ctrlThis.ratedItems[ctrlThis.lsData.id] = value;
            ctrlThis.scored.emit({
                [ctrlThis.lsData.questionId]: value
            });
        });

        // This does the wrapping for scale guides
        axis.selectAll('.tick text').call(wrap, this.xLabel.bandwidth());
        // axis.selectAll('.x g.tick').attr('class', 'lift-ticks')
        axis.selectAll('text')
            .attr('class', 'axis-label');
        axis.selectAll('line')
            .attr('class', 'tick-mark')
            .attr('stroke', this.fillColor);
        axis.selectAll('path')
            .attr('stroke', this.fillColor);

    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.lsData && changes.lsData.currentValue) {

            if (changes.lsData.firstChange) {
                // console.log('First change');

                /**
                 * @description Use this block for ShadowDOM encapsulation
                 * Does not work atm. Reverted to normal encapsulation for now
                 */
                // const nodes: NodeList = this.el.nativeElement.shadowRoot.childNodes;
                const container: HTMLBaseElement = this.el.nativeElement;
                this.chartBox = select(container);

                const height: number = this.margin.top + this.margin.bottom + this.axisHeight + 50;
                const { scale_text, items } = this.lsData;

                // Override vertical margin between linescale rating labels
                if (this.vMargin) {
                    // this.margin.bottom
                }

                this.drawLinescale(scale_text, height);

                if (this.lsData.value) {
                    this.drawScore(Math.floor(this.lsData.value), Math.trunc(this.lsData.scaled_value * 100) / 100);
                }

                /**
                 * Render previously rated items
                 */
                if (items) {
                    // Make a copy to not mutate component input
                    let subset: LsItem[] = items.slice();
                    // FIXME: Why is this not being used?
                    const myThings = subset.filter(t => t.value);
                    // console.log('All length w Val: ', myThings.length);

                    // Reverse the order to last rated to first 
                    // subset.reverse();
                    // const anchorItem = items.find(item => item.is_anchor);
                    const anchorItems = items.filter(item => item.is_anchor);
                    if (this.limit && subset.length > this.limit) {

                        // subset = subset.slice(0, this.limit);
                        // console.log('Limit: ', this.limit);

                        const startPos = subset.length - this.limit;
                        const endPos = subset.length;
                        // console.log('start: ', startPos);
                        // console.log('end: ', endPos);

                        subset = subset.slice(startPos, endPos);
                        // console.log(subset.length, anchorItem);

                    }
                    // TODO: Searching by reference I think...?
                    // if (subset.length && anchorItems.length && !subset.includes(anchorItem)) {
                    if (subset.length && anchorItems.length) {
                        console.log('Should not be here...');
                        let pos: number = 0;
                        anchorItems.forEach(anchItem => {
                            if (!subset.includes(anchItem)) {
                                while (subset[pos].is_anchor && pos < subset.length - 1) {
                                    pos++;
                                }
                                subset[pos] = anchItem;
                                pos++;
                            }
                        });
                    }

                    // Filter out any items with no values
                    // TODO: Rework this. Feels redundant/out of place
                    subset = subset.filter(item => item.value);

                    // console.log('Items before: ', this.items.length);
                    subset.forEach(item => this.addRating(item.label, item.value, this.threshhold, item.is_anchor));
                    // console.log('After: ', this.items.length);

                    this.renderLabels();

                    /**
                     * Resize the SVG element to accommodate rating heights
                     */
                    if (this.items.length) {
                        this.resizeSVG();
                    }
                }

            }
            if (this.lsData.text) {
                this.text = this.sanitizer.bypassSecurityTrustHtml(this.lsData.text);
            }
            if (this.lsData.prelim) {
                this.prelim = this.sanitizer.bypassSecurityTrustHtml(this.lsData.prelim);
            }

            this._isQuestion = this.lsData.is_question;
        }

        this._isQuestion = changes.isQuestion ? changes.isQuestion.currentValue : this._isQuestion;

    }

    ngAfterViewInit() {
      // TODO: This skip logic may be removed due to transition to dedicated skip button
        const el: HTMLElement = this.el.nativeElement;
        const spans = el.getElementsByClassName('link');

        if (spans.length) {
            const span = spans[0],
                text: string = span.textContent;
            const oldTextNode = span.childNodes[0];
            const linkNode = this.renderer.createElement('a'),
                textNode = this.renderer.createText(text);

            if (this.skipUrl)
                this.renderer.setAttribute(linkNode, 'href', this.skipUrl);
            else {
                this.renderer.setAttribute(linkNode, 'href', '#');
                let eventRes;
                const clickHandler = (e: MouseEvent) => {
                    console.log('Here I am...');

                    e.preventDefault();
                    this.skip.emit();
                    eventRes();
                };
                eventRes = this.renderer.listen(linkNode, 'click', clickHandler);
            }

            this.renderer.removeChild(span, oldTextNode);
            this.renderer.appendChild(linkNode, textNode);
            this.renderer.appendChild(span, linkNode);
        }
    }

    ngOnDestroy() {
        // Remove event listener on linescale
        this.line.on('click', null);
    }

}

